import React, { useState } from "react";
import { Download, Watch } from "lucide-react";
import type { reportingPeriod } from "@duress/shared";
import { fmtHours, fmtInt, fmtPct } from "@/lib/format";
import { DataTable, type DataTableColumn } from "@/components/DataTable";
import { EmptyState } from "@/components/EmptyState";
import { ErrorCard } from "@/components/ErrorCard";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { Button } from "@/components/ui/button";
import { KPIGrid, type KPITile } from "../dashboard/KPIGrid";
import { downloadDeviceList } from "../api";
import { useDashboardView, type SelectedView } from "@/features/periods/usePeriods";

type DeviceTypeBreakdown = reportingPeriod.DeviceTypeBreakdown;
type DeviceTypeMetrics = reportingPeriod.DeviceTypeMetrics;
type StoreMixItem = reportingPeriod.StoreMixItem;

// ── Cell renderers ────────────────────────────────────────────────────────────

const numCell = (v: unknown): React.ReactNode => (
  <span className="num">{fmtInt(typeof v === "number" ? v : Number(v))}</span>
);

const pctCell = (v: unknown): React.ReactNode => (
  <span className="num">{fmtPct(typeof v === "number" ? v : Number(v))}</span>
);

const teamCell = (v: unknown): React.ReactNode => (
  <span className="font-head font-medium">{typeof v === "string" ? v : ""}</span>
);

// ── Columns ──────────────────────────────────────────────────────────────────

const MIX_COLUMNS: DataTableColumn<StoreMixItem>[] = [
  { key: "store", label: "Team", render: teamCell },
  { key: "total_devices", label: "Total Devices", render: numCell },
  { key: "watch_count", label: "Watch", render: numCell },
  { key: "watch_pct", label: "Watch %", render: pctCell },
  { key: "bodyworn_count", label: "Bodyworn", render: numCell },
  { key: "bodyworn_pct", label: "Bodyworn %", render: pctCell },
];

// ── Tile helpers ──────────────────────────────────────────────────────────────

function buildTiles(dt: DeviceTypeBreakdown): KPITile[] {
  return [
    { label: "Total Watch Devices", value: fmtInt(dt.Watch.device_count) },
    { label: "Total Bodyworn Devices", value: fmtInt(dt.Bodyworn.device_count) },
    { label: "Watch Productive Hrs", value: fmtHours(dt.Watch.productive_hours) },
    { label: "Bodyworn Productive Hrs", value: fmtHours(dt.Bodyworn.productive_hours) },
  ];
}

// ── Comparison card ──────────────────────────────────────────────────────────

function ComparisonCard({
  label,
  metrics,
}: {
  label: "Watch" | "Bodyworn";
  metrics: DeviceTypeMetrics;
}): React.JSX.Element {
  if (metrics.sessions === 0) {
    return (
      <div
        className="card-duress p-5"
        data-device-type={label.toLowerCase()}
        data-device-type-state="empty"
      >
        <p className="font-head font-semibold text-sm text-foreground mb-4">{label}</p>
        <div className="py-8 text-center">
          <p className="text-sm text-muted-foreground">
            No {label} sessions in this period.
          </p>
        </div>
      </div>
    );
  }

  const rows: { label: string; value: string }[] = [
    { label: "Sessions", value: fmtInt(metrics.sessions) },
    { label: "Productive Hrs", value: fmtHours(metrics.productive_hours) },
    { label: "Total Hrs", value: fmtHours(metrics.total_hours) },
    { label: "Extended Rate", value: fmtPct(metrics.extended_rate) },
    { label: "Avg Session Hrs", value: fmtHours(metrics.avg_session_hours) },
    { label: "Devices", value: fmtInt(metrics.device_count) },
  ];

  return (
    <div
      className="card-duress p-5"
      data-device-type={label.toLowerCase()}
      data-device-type-state="populated"
    >
      <p className="font-head font-semibold text-sm text-foreground mb-4">{label}</p>
      <div className="grid grid-cols-2 gap-x-6 gap-y-3">
        {rows.map((r) => (
          <div key={r.label} className="flex items-baseline justify-between">
            <span className="text-xs text-muted-foreground">{r.label}</span>
            <span className="num text-sm font-medium">{r.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Pure presentational component ────────────────────────────────────────────

export interface DeviceTypeAnalysisProps {
  data: reportingPeriod.DashboardData;
}

export function DeviceTypeAnalysis({ data }: DeviceTypeAnalysisProps): React.JSX.Element {
  const dt = data.methodology.device_types;

  if (
    dt.Watch.sessions === 0 &&
    dt.Bodyworn.sessions === 0 &&
    dt.store_mix.length === 0
  ) {
    return (
      <div data-device-type-state="empty">
        <EmptyState icon={Watch} body="No device-type data in this period." />
      </div>
    );
  }

  const tiles = buildTiles(dt);

  return (
    <div className="space-y-6">
      <KPIGrid tiles={tiles} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-[10px]">
        <ComparisonCard label="Watch" metrics={dt.Watch} />
        <ComparisonCard label="Bodyworn" metrics={dt.Bodyworn} />
      </div>

      <div className="card-duress p-5">
        <p className="font-head font-semibold text-sm text-foreground mb-4">
          Deployment Mix
        </p>
        <DataTable<StoreMixItem>
          columns={MIX_COLUMNS}
          data={dt.store_mix}
          defaultSort={{ key: "total_devices", dir: "desc" }}
          searchKeys={["store"]}
          emptyMessage="No deployment-mix data in this period."
        />
      </div>

      <div className="finding-blue">
        <p className="font-head font-semibold text-sm mb-1 text-pill-blue">Methodology Note</p>
        <p className="text-sm text-foreground">
          <strong>Device counts</strong> are unique by <em>Watch Name</em> (device serial) within
          each device type. <strong>Extended Rate</strong> is the share of that type’s sessions
          exceeding the extended threshold. <strong>Deployment Mix</strong> percentages show each
          team’s watch / bodyworn share of its deployed devices — the two %-columns sum to 100% per
          row. A device type with zero sessions in the period still appears in the comparison cards
          as a “no sessions” message rather than being hidden, so the contract shape is always
          predictable.
        </p>
      </div>
    </div>
  );
}

// ── Smart wrapper that fetches data ───────────────────────────────────────────

function DeviceListExportButton({ clientSlug }: { clientSlug: string }): React.JSX.Element {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleClick = async (): Promise<void> => {
    setIsExporting(true);
    setExportError(null);
    try {
      await downloadDeviceList(clientSlug);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : "Export failed");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="flex items-center justify-end gap-3">
      {exportError && <p className="text-sm font-medium text-pill-red">{exportError}</p>}
      <Button
        variant="ghost"
        size="sm"
        className="gap-1.5"
        disabled={isExporting}
        onClick={() => {
          void handleClick();
        }}
        title="Download every device for this client, with a per-team breakdown (all periods)"
      >
        <Download className="h-3.5 w-3.5" />
        {isExporting ? "Exporting…" : "Export device list"}
      </Button>
    </div>
  );
}

export function DeviceTypeAnalysisTab({
  clientId,
  clientSlug,
  view,
}: {
  clientId: string;
  clientSlug: string;
  view: SelectedView | null;
}): React.JSX.Element {
  const { data, isLoading, error, refetch } = useDashboardView(clientId, view);

  if (!view) {
    return <EmptyState body="Select a reporting period above." />;
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <LoadingSkeleton variant="kpis" />
        <LoadingSkeleton variant="chart" />
      </div>
    );
  }

  if (error || !data) {
    return <ErrorCard message="Failed to load period data." onRetry={refetch} />;
  }

  return (
    <div className="space-y-6">
      <DeviceListExportButton clientSlug={clientSlug} />
      <DeviceTypeAnalysis data={data} />
    </div>
  );
}
